import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiClient } from "../services/apiClient";
import { fleetService } from "../services/fleetService";
import { useI18n } from "../providers/I18nProvider";
import { useConfirm } from "../providers/ConfirmProvider";
import ReservationFormModal from "../components/fleet/ReservationFormModal";
import ReservationCard from "../components/fleet/ReservationCard";
import NoirAlert from "../components/NoirAlert";
import PaginationControls from "../components/PaginationControls";
import { TabBar, FilterBar, SearchInput, FilterField, EmptyState } from "../components/ListToolbar";
import {
  categorize,
  filterReservations,
  sortReservations,
} from "../components/fleet/reservationUtils";
import "../styles/Fleet.css";

const PAGE_SIZE_KEY = "pageSize.myReservations";

const MyReservationsPage = () => {
  const confirm = useConfirm();
  const { t } = useI18n();
  const [reservations, setReservations] = useState([]);
  const [me, setMe] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [notification, setNotification] = useState({ message: "", type: "" });
  const clearNotification = useCallback(() => setNotification({ message: "", type: "" }), []);
  const busyRef = useRef(new Set());

  const [tab, setTab] = useState("upcoming");
  const [search, setSearch] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const [roleFilter, setRoleFilter] = useState("all"); // all | owner | participant
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sortKey, setSortKey] = useState("start_asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    const parsed = parseInt(localStorage.getItem(PAGE_SIZE_KEY) || "10", 10);
    return [10, 20, 30].includes(parsed) ? parsed : 10;
  });

  const load = useCallback(() => {
    setError("");
    return fleetService
      .myReservations()
      .then((data) => setReservations(Array.isArray(data) ? data : []))
      .catch((err) => setError(err.response?.data?.msg || t("fleet.loadError")))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => {
    apiClient
      .get("/users/me")
      .then((r) => setMe(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const c = { upcoming: 0, past: 0, closed: 0, all: reservations.length };
    const now = Date.now();
    reservations.forEach((r) => {
      const cat = categorize(r, now);
      if (cat === "closed") c.closed += 1;
      else if (cat === "past") c.past += 1;
      else if (new Date(r.ends_at).getTime() >= now) c.upcoming += 1; // bekleyen + yaklaşan
    });
    return c;
  }, [reservations]);

  const vehicles = useMemo(() => {
    const map = new Map();
    reservations.forEach((r) => map.set(String(r.vehicle_id), r.vehicle_name));
    return [...map.entries()].sort((a, b) => String(a[1]).localeCompare(String(b[1]), "tr"));
  }, [reservations]);

  const filtered = useMemo(() => {
    const base = filterReservations(reservations, { tab, search, vehicleId, from, to });
    const byRole =
      roleFilter === "all" || !me
        ? base
        : base.filter((r) => (roleFilter === "owner" ? r.user_id === me.id : r.user_id !== me.id));
    // yaklaşan: en yakın üstte, geçmiş: en yeni üstte (kullanıcı değiştirmediyse)
    const effectiveSort = tab === "past" && sortKey === "start_asc" ? "start_desc" : sortKey;
    return sortReservations(byRole, effectiveSort);
  }, [reservations, tab, search, vehicleId, from, to, roleFilter, sortKey, me]);

  useEffect(() => {
    setPage(1);
  }, [tab, search, vehicleId, roleFilter, from, to, sortKey, pageSize]);

  useEffect(() => {
    localStorage.setItem(PAGE_SIZE_KEY, String(pageSize));
  }, [pageSize]);

  const paged = useMemo(
    () => filtered.slice((page - 1) * pageSize, page * pageSize),
    [filtered, page, pageSize]
  );

  const hasActiveFilter = Boolean(search || vehicleId || from || to || roleFilter !== "all");
  const resetFilters = () => {
    setSearch("");
    setVehicleId("");
    setFrom("");
    setTo("");
    setRoleFilter("all");
  };

  const cancel = async (id) => {
    if (busyRef.current.has(id)) return;
    if (!(await confirm(t("fleet.cancelConfirm")))) return;
    busyRef.current.add(id);
    try {
      await fleetService.cancelReservation(id);
      setNotification({ message: t("fleetUi.cancelDone"), type: "success" });
      await load();
    } catch (err) {
      setNotification({
        message: err.response?.data?.msg || t("fleet.errors.generic"),
        type: "error",
      });
    } finally {
      busyRef.current.delete(id);
    }
  };

  const tabs = [
    { key: "upcoming", label: t("fleetUi.tabs.upcoming"), count: counts.upcoming },
    { key: "past", label: t("fleetUi.tabs.past"), count: counts.past },
    { key: "closed", label: t("fleetUi.tabs.closed"), count: counts.closed },
    { key: "all", label: t("fleetUi.tabs.all"), count: counts.all },
  ];

  return (
    <div className="fleet-page">
      <NoirAlert message={notification.message} type={notification.type} onClose={clearNotification} />
      <div className="fleet-page-header">
        <div>
          <h2>{t("fleet.myReservations")}</h2>
          <div className="subtitle">{t("fleet.myReservationsSubtitle")}</div>
        </div>
      </div>

      {error && (
        <div className="fleet-error" role="alert">
          {error}{" "}
          <button type="button" className="fleet-btn secondary" onClick={load}>
            {t("fleetUi.retry")}
          </button>
        </div>
      )}
      {loading ? (
        <div className="fleet-empty">{t("common.loading")}</div>
      ) : (
        <>
          <TabBar tabs={tabs} value={tab} onChange={setTab} />
          <FilterBar showReset={hasActiveFilter} onReset={resetFilters}>
            <SearchInput value={search} onChange={setSearch} placeholder={t("fleetUi.filters.search")} />
            <select
              className="leaves-filter-input lt-input"
              value={vehicleId}
              onChange={(e) => setVehicleId(e.target.value)}
              aria-label={t("fleetUi.filters.vehicle")}
            >
              <option value="">{t("fleetUi.filters.allVehicles")}</option>
              {vehicles.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
            <select
              className="leaves-filter-input lt-input"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              aria-label={t("fleetUi.filters.role")}
            >
              <option value="all">{t("fleetUi.filters.roleAll")}</option>
              <option value="owner">{t("fleetUi.filters.roleOwner")}</option>
              <option value="participant">{t("fleetUi.filters.roleParticipant")}</option>
            </select>
            <FilterField label={t("fleetUi.filters.from")}>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </FilterField>
            <FilterField label={t("fleetUi.filters.to")}>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </FilterField>
            <select
              className="leaves-filter-input lt-input"
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value)}
              aria-label={t("fleetUi.filters.sort")}
            >
              <option value="start_asc">{t("fleetUi.filters.sortStartAsc")}</option>
              <option value="start_desc">{t("fleetUi.filters.sortStartDesc")}</option>
            </select>
          </FilterBar>

          {filtered.length === 0 ? (
            <EmptyState
              message={reservations.length === 0 ? t("fleet.noReservations") : t("fleetUi.emptyFiltered")}
            />
          ) : (
            <>
              <PaginationControls
                totalItems={filtered.length}
                page={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
              />
              <div className="reservation-list">
                {paged.map((r) => {
                  const isOwner = me?.id === r.user_id;
                  const isFuture = new Date(r.starts_at) > new Date();
                  const canCancel = isOwner && isFuture && (r.status === "approved" || r.status === "pending");
                  const canEdit = canCancel;
                  return (
                    <ReservationCard
                      key={r.id}
                      r={r}
                      showOwner={!isOwner}
                      badge={me && !isOwner ? t("fleetUi.participantBadge") : null}
                      actions={
                        (canEdit || canCancel) && (
                          <div className="actions">
                            {canEdit && (
                              <button className="fleet-btn secondary" onClick={() => setEditing(r)} type="button">
                                {t("common.edit")}
                              </button>
                            )}
                            {canCancel && (
                              <button className="fleet-btn danger" onClick={() => cancel(r.id)} type="button">
                                {t("fleet.cancel")}
                              </button>
                            )}
                          </div>
                        )
                      }
                    />
                  );
                })}
              </div>
              <PaginationControls
                totalItems={filtered.length}
                page={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
              />
            </>
          )}
        </>
      )}

      {editing && me && (
        <ReservationFormModal
          vehicle={{ id: editing.vehicle_id, name: editing.vehicle_name }}
          existingReservation={editing}
          currentUserId={me.id}
          onClose={() => setEditing(null)}
          onUpdated={() => {
            setEditing(null);
            setNotification({ message: t("fleetUi.saved"), type: "success" });
            load();
          }}
        />
      )}
    </div>
  );
};

export default MyReservationsPage;
